import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useId, useRef, useState } from 'react';
import {
  canConvertUnit,
  formatNumber,
  getUnit,
  toBaseQuantity,
  type BaseUnit,
  type IngredientPackage,
} from '@crave/costing';
import { Link } from 'react-router';
import { Button, Card, Field, FormGrid, SelectField } from '@crave/ui';
import { api } from '../api.ts';
import {
  MEASURE_STYLE_OPTIONS,
  formatQty,
  useMeasureStyle,
  type MeasureStyle,
} from '../components/MeasureIn.tsx';
import {
  UnitSelect,
  defaultUnitValue,
  splitUnitValue,
  unitChoiceLabel,
  unitValue,
} from '../components/UnitSelect.tsx';
import { Notice, Section, errorText, money, today, when } from '../components/ui.tsx';
import { useStaff } from '../lib/staff.tsx';

/** What was typed for a hand-entered price, as the lot keeps it. */
interface PriceEntered {
  price_cents: number;
  price_for: 'amount' | 'total';
  quantity: number | null;
  unit: string | null;
  package_id: string | null;
  package_label: string | null;
  free: boolean;
}
interface Lot {
  id: string;
  received_at: string;
  expires_at: string | null;
  remaining_qty: number;
  initial_qty: number;
  total_cost_cents: number;
  vendor: string | null;
  vendor_id: string | null;
  /** Priced by a receipt: corrected through that receipt, never here. */
  from_receipt: boolean;
  /** A price added for stock that was already on hand. */
  is_opening_price: boolean;
  can_correct_price: boolean;
  price_entered: PriceEntered | null;
  price_corrected_at: string | null;
}
interface Vendor {
  id: string;
  name: string;
  is_active: boolean;
}
interface PriceResult {
  ok: boolean;
  lot_id: string;
  qty_base?: number;
  total_cost_cents: number;
  cost_cents_per_base: number;
  on_hand_qty: number;
}
interface Stock {
  id: string;
  code: string;
  name: string;
  base_unit: BaseUnit;
  category: string | null;
  is_prep: boolean;
  display_unit: string | null;
  density_g_per_ml: number | null;
  each_weight_g: number | null;
  packages: IngredientPackage[] | null;
  on_hand_qty: number;
  reserved_qty: number;
  available_qty: number;
  cost_cents_per_base: number | null;
  cost_source: string | null;
  last_purchased_at: string | null;
  /** What the lots on hand hold: the part of on hand that has a price. */
  priced_qty: number;
  /** On hand that no lot covers, so it has no price yet. */
  uncovered_qty: number;
  lots: Lot[] | null;
}
interface StockResponse {
  location: string;
  location_id: string;
  items: Stock[];
}
interface PrepRecipe {
  id: string;
  code: string;
  name: string;
  produces_ingredient_id: string;
}
interface AdjustResult {
  ok: boolean;
  delta_base: number;
  on_hand_after: number;
}

type Mode = 'remove' | 'add' | 'set';
const MODES: [Mode, string][] = [
  ['remove', 'Remove'],
  ['add', 'Add'],
  ['set', 'Set count to'],
];

/** Plain reasons per action. `code` is what the API records; `key` keeps the choices apart. */
interface ReasonChoice {
  key: string;
  code: string;
  label: string;
  needsNote?: boolean;
}
const REASONS: Record<Mode, ReasonChoice[]> = {
  remove: [
    { key: 'waste', code: 'WASTE', label: 'Waste' },
    { key: 'spillage', code: 'SPILLAGE', label: 'Spillage' },
    { key: 'meal', code: 'EMPLOYEE_MEAL', label: 'Employee meal' },
    { key: 'sampling', code: 'SAMPLING', label: 'Tasting or sampling' },
    { key: 'testing', code: 'TESTING', label: 'Testing' },
    { key: 'correction', code: 'MANUAL_CORRECTION', label: 'Count correction' },
  ],
  add: [
    { key: 'found', code: 'PHYSICAL_COUNT', label: 'Found stock' },
    { key: 'correction', code: 'MANUAL_CORRECTION', label: 'Count correction' },
    {
      key: 'other',
      code: 'MANUAL_CORRECTION',
      label: 'Other (say why in the note)',
      needsNote: true,
    },
  ],
  set: [{ key: 'count', code: 'PHYSICAL_COUNT', label: 'Physical count' }],
};

const MEASURE_LABELS: Record<MeasureStyle, string> = {
  ...(Object.fromEntries(MEASURE_STYLE_OPTIONS) as Record<MeasureStyle, string>),
  entered: 'As each ingredient is set',
};

const STOCK_HEAD = ['Ingredient', 'On hand', 'Reserved', 'Available', 'Cost', 'Lots', ''];

/*
 * The stock table; in a narrow space each ingredient becomes a labelled block: name and Adjust on
 * top, the three amounts in a row, then cost and lots. The measure control copies the look of the
 * shared MeasureIn so it matches the Recipes page.
 */
const INVENTORY_CSS = `
.inv-stock { container-type: inline-size; min-width: 0; }
.inv-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.inv-table th { text-align: left; padding: 8px 10px; border-bottom: 2px solid var(--line); color: var(--ink-soft); font-weight: 600; white-space: nowrap; }
.inv-table td { padding: 8px 10px; border-bottom: 1px solid var(--line); vertical-align: middle; }
.inv-table td.inv-amt { white-space: nowrap; }
.inv-table td.inv-act { text-align: right; }
.inv-table td.inv-empty { padding: 16px; color: var(--ink-soft); }
.inv-label { display: none; }
.inv-hidden { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
.inv-link { display: inline-flex; align-items: center; background: none; border: 0; color: var(--crave-red); cursor: pointer;
  padding: 0; min-height: var(--touch); font: inherit; font-weight: 600; white-space: nowrap; }
.inv-adjust-form { scroll-margin-top: 88px; }
.inv-table td.inv-lots-cell { background: var(--mist); padding: 10px 12px 12px; }
.inv-lots-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
.inv-lots-list { list-style: none; margin: 4px 0 0; padding: 0; display: grid; gap: 6px; font-size: 14px; }
.inv-lots-list li { overflow-wrap: anywhere; }
.inv-sub { display: block; font-size: 12px; line-height: 1.3; color: var(--ink-soft); }
.inv-unpriced { display: grid; justify-items: start; }
.inv-lot-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 0 12px; }
.inv-price-form { display: grid; gap: 12px; }
.inv-price-form p { margin: 0; font-size: 14px; color: var(--ink-soft); }
.inv-check { display: flex; align-items: center; gap: 10px; min-height: 44px; font-size: 15px; cursor: pointer; }
.inv-check input { width: 22px; height: 22px; margin: 0; flex: none; accent-color: var(--crave-red); }
.inv-choice .crave-measure-option span { min-height: 44px; }
@container (max-width: 640px) {
  .inv-table thead { display: none; }
  .inv-table, .inv-table tbody { display: block; }
  .inv-table tr { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); grid-template-areas: 'name name act' 'oh res av' 'cost cost lots';
    column-gap: 12px; row-gap: 8px; padding: 12px 0; border-bottom: 1px solid var(--line); }
  .inv-table td { display: block; padding: 0; border: 0; min-width: 0; }
  .inv-table td.inv-amt { white-space: normal; }
  .inv-table td.inv-name { grid-area: name; }
  .inv-table td.inv-act { grid-area: act; align-self: start; }
  .inv-table td.inv-oh { grid-area: oh; }
  .inv-table td.inv-res { grid-area: res; }
  .inv-table td.inv-av { grid-area: av; }
  .inv-table td.inv-cost { grid-area: cost; }
  .inv-table td.inv-lots { grid-area: lots; }
  .inv-table td.inv-empty { grid-column: 1 / -1; }
  .inv-table tr.inv-lots-row { display: block; padding: 0 0 12px; }
  .inv-table td.inv-lots-cell { padding: 10px 12px; border-radius: 8px; }
  .inv-label { display: block; font-size: 12px; line-height: 1.3; color: var(--ink-soft); }
}
.crave-measure { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 10px; border: 0; margin: 0; padding: 0; min-width: 0; }
.crave-measure > legend { float: left; padding: 0; margin: 0; font-weight: 600; font-size: 15px; white-space: nowrap; }
.crave-measure-options { display: flex; flex-wrap: wrap; gap: 4px; padding: 3px; min-width: 0;
  border: 1px solid var(--line); border-radius: 10px; background: var(--mist); }
.crave-measure-option { position: relative; display: inline-flex; }
.crave-measure-option input { position: absolute; inset: 0; opacity: 0; margin: 0; cursor: pointer; }
.crave-measure-option span { display: inline-flex; align-items: center; min-height: var(--touch); padding: 0 12px;
  border-radius: 7px; font-size: 14px; font-weight: 600; color: var(--ink-soft); white-space: nowrap; }
.crave-measure-option input:checked + span { background: var(--paper); color: var(--ink);
  box-shadow: 0 1px 3px rgba(20, 20, 20, 0.16); outline: 1px solid var(--line); }
.crave-measure-option input:focus-visible + span { outline: 3px solid var(--crave-red); outline-offset: 1px; }
@media (max-width: 560px) {
  .crave-measure { display: grid; }
  .crave-measure-options { display: grid; grid-template-columns: repeat(auto-fit, minmax(118px, 1fr)); }
  .crave-measure-option span { min-height: var(--touch); width: 100%; justify-content: center; white-space: normal; text-align: center; line-height: 1.2; padding: 4px 8px; }
}
`;

/** "Measure in" with the first choice worded for stock: each ingredient's own display unit. */
function StockMeasureIn() {
  const [style, setStyle] = useMeasureStyle();
  const name = useId();
  return (
    <fieldset className="crave-measure">
      <legend>Measure in</legend>
      <div className="crave-measure-options">
        {MEASURE_STYLE_OPTIONS.map(([v]) => (
          <label key={v} className="crave-measure-option">
            <input
              type="radio"
              name={name}
              value={v}
              checked={style === v}
              onChange={() => setStyle(v)}
            />
            <span>{MEASURE_LABELS[v]}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** The unit a cost reads best in for this style: "$1.99 / lb", "$4.39 / kg", "$0.45 each". */
function costUnit(s: Stock, style: MeasureStyle): string {
  const fits = (u: string | null | undefined): u is string => !!u && canConvertUnit(u, s);
  const metric = s.base_unit === 'g' ? 'kg' : s.base_unit === 'ml' ? 'l' : 'each';
  const tries: (string | null)[] =
    style === 'entered'
      ? [s.display_unit, metric]
      : style === 'metric'
        ? [metric]
        : style === 'us_weight'
          ? [s.base_unit === 'ml' && s.density_g_per_ml === null ? 'floz' : 'lb', metric]
          : style === 'us_volume'
            ? [s.base_unit === 'each' ? null : 'cup', 'lb', metric]
            : [s.base_unit === 'ml' ? 'imp_pt' : 'lb', metric];
  return tries.find(fits) ?? s.base_unit;
}

function costText(s: Stock, style: MeasureStyle): string | null {
  if (s.cost_cents_per_base === null) return null;
  const unit = costUnit(s, style);
  let perUnit: number;
  try {
    perUnit = s.cost_cents_per_base * toBaseQuantity(1, unit, s);
  } catch {
    return null;
  }
  const dollars = perUnit / 100;
  const text = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: dollars > 0 && dollars < 0.1 ? 4 : 2,
  }).format(dollars);
  return unit === 'each' ? `${text} each` : `${text} / ${getUnit(unit)?.abbr ?? unit}`;
}

/** What was chosen, as a short name: "lb", "cup", "16 oz tub". */
function unitShort(value: string, ing: Stock | undefined): string {
  const f = splitUnitValue(value);
  if ('unit' in f && f.unit) return getUnit(f.unit)?.abbr ?? f.unit;
  if ('package_id' in f && f.package_id) {
    return ing?.packages?.find((p) => p.id === f.package_id)?.label ?? 'package';
  }
  return unitChoiceLabel(value, ing);
}

/** The typed amount in the ingredient's base unit, or null when it does not convert. */
function toBase(ing: Stock, quantity: number, value: string): number | null {
  const f = splitUnitValue(value);
  try {
    if ('package_id' in f && f.package_id)
      return toBaseQuantity(quantity, { packageId: f.package_id }, ing);
    if ('unit' in f && f.unit) return toBaseQuantity(quantity, f.unit, ing);
  } catch {
    return null;
  }
  return null;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

type AdjustErrors = Partial<
  Record<'ingredient' | 'quantity' | 'unit' | 'note' | 'cost', string | undefined>
>;

const blankAdjust = {
  ingredient_id: '',
  mode: 'remove' as Mode,
  quantity: '',
  unit: '',
  reason: 'waste',
  cost: '',
  /** A cost of 0 is saved only when this is ticked. */
  free: false,
  note: '',
};

export function Inventory() {
  const qc = useQueryClient();
  const { can, location } = useStaff();
  const canAdjust = can('inventory.manage');
  // Prices are a purchasing matter: owners and managers add and correct them.
  const canPrice = can('purchasing.manage');
  const vendors = useQuery({
    queryKey: ['vendors'],
    enabled: canPrice,
    queryFn: async () =>
      (await api.get<{ items: Vendor[] }>('/admin/inventory/vendors')).data.items,
  });
  /** The open price form: a new price for the unpriced stock (lotId null) or a correction. */
  const [pricing, setPricing] = useState<{ id: string; lotId: string | null } | null>(null);
  const [priceMsg, setPriceMsg] = useState<string | null>(null);
  const [style] = useMeasureStyle();
  // Stock carries the location id, so team members (inventory.read) never need /admin/locations.
  const stock = useQuery({
    queryKey: ['stock', location.slug],
    queryFn: async () =>
      (await api.get<StockResponse>(`/admin/inventory/stock?location=${location.slug}`)).data,
  });
  const prepRecipes = useQuery({
    queryKey: ['prep-recipes'],
    queryFn: async () =>
      (await api.get<{ items: PrepRecipe[] }>('/admin/inventory/prep-recipes')).data.items,
  });
  const [adjMsg, setAdjMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [prodMsg, setProdMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [adj, setAdj] = useState(blankAdjust);
  const [errors, setErrors] = useState<AdjustErrors>({});
  const [prod, setProd] = useState({ recipe_id: '', batches: '1' });
  const [open, setOpen] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const quantityId = useId();

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['stock'] });
    void qc.invalidateQueries({ queryKey: ['shopping'] });
  };
  const items = stock.data?.items;
  const byId = new Map((items ?? []).map((s) => [s.id, s]));
  const locationId = stock.data?.location_id;
  const noLocationText = stock.isError
    ? `Stock did not load, so this can't be saved. ${errorText(stock.error, '')}`
    : stock.isLoading
      ? 'Stock is still loading. Try again in a moment.'
      : 'Could not find this location. Reload the page and try again.';

  const adjust = useMutation({
    mutationFn: async (v: {
      body: Record<string, unknown>;
      ing: Stock;
      mode: Mode;
      amount: string;
    }) => ({
      ...v,
      result: (await api.post<AdjustResult>('/admin/inventory/adjust', v.body)).data,
    }),
    onSuccess: ({ ing, mode, amount, result }) => {
      const now = formatQty(result.on_hand_after, ing, style);
      const text =
        mode === 'remove'
          ? `Removed ${amount} of ${ing.name}. On hand now: ${now}.`
          : mode === 'add'
            ? `Added ${amount} of ${ing.name}. On hand now: ${now}.`
            : Math.abs(result.delta_base) < 1e-6
              ? `Counted ${ing.name} at ${amount}. That matches what was on hand, so nothing changed.`
              : `Counted ${ing.name} at ${amount}. On hand now: ${now}.`;
      setAdjMsg({ kind: 'ok', text });
      setAdj((a) => ({ ...a, quantity: '', cost: '', free: false, note: '' }));
      refresh();
    },
    onError: (e) =>
      setAdjMsg({ kind: 'error', text: errorText(e, 'Could not save. Check the connection.') }),
  });
  const produce = useMutation({
    mutationFn: (v: Record<string, unknown>) => api.post('/admin/inventory/production', v),
    onSuccess: () => {
      setProdMsg({ kind: 'ok', text: 'Production run saved. Stock is updated.' });
      refresh();
    },
    onError: (e) => setProdMsg({ kind: 'error', text: errorText(e) }),
  });

  const ing = byId.get(adj.ingredient_id);
  const reasons = REASONS[adj.mode];
  const reason = reasons.find((r) => r.key === adj.reason) ?? reasons[0]!;
  const quantityText = adj.quantity.trim();
  const quantity = Number(quantityText);
  const base =
    ing && quantityText && Number.isFinite(quantity) && quantity >= 0 && adj.unit
      ? toBase(ing, quantity, adj.unit)
      : null;

  const preview = (() => {
    if (!ing) return 'Choose an ingredient first.';
    if (!quantityText) {
      return adj.mode === 'remove'
        ? 'How much to take out.'
        : adj.mode === 'add'
          ? 'How much to put in.'
          : 'What you counted on the shelf.';
    }
    if (!Number.isFinite(quantity)) return 'Enter a number, like 2 or 1.5.';
    if (quantity < 0) return 'Enter 0 or more. Choose Remove to take stock out.';
    if (base === null) return adj.unit ? 'This unit does not convert yet.' : 'Choose a unit.';
    // In the "Measure in" choice, like the table above and the saved message.
    const amount = (n: number) => `${n < 0 ? '−' : ''}${formatQty(Math.abs(n), ing, style)}`;
    const onHand = ing.on_hand_qty;
    if (adj.mode === 'set') {
      const change = base - onHand;
      return Math.abs(change) < 1e-6
        ? `No change (was ${amount(onHand)}).`
        : `Change: ${change > 0 ? '+' : '−'}${formatQty(Math.abs(change), ing, style)} (was ${amount(onHand)}).`;
    }
    const after = adj.mode === 'remove' ? onHand - base : onHand + base;
    return `That is ${amount(base)}. On hand after: ${amount(after)}.`;
  })();

  const chooseIngredient = (id: string) => {
    setAdj((a) => ({ ...a, ingredient_id: id, unit: defaultUnitValue(byId.get(id)) }));
    setErrors({});
  };
  const chooseMode = (mode: Mode) => {
    setAdj((a) => ({
      ...a,
      mode,
      reason: REASONS[mode][0]!.key,
      cost: mode === 'add' ? a.cost : '',
      free: mode === 'add' ? a.free : false,
    }));
    setErrors({});
  };
  const startAdjust = (s: Stock) => {
    chooseIngredient(s.id);
    setAdjMsg(null);
    const form = formRef.current;
    if (!form) return;
    form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(
      () => document.getElementById(quantityId)?.focus({ preventScroll: true }),
      300,
    );
  };

  const submitAdjust = () => {
    setAdjMsg(null);
    const next: AdjustErrors = {};
    if (!ing) next.ingredient = 'Choose an ingredient.';
    if (!quantityText || !Number.isFinite(quantity) || quantity < 0) {
      next.quantity = 'Enter 0 or more, like 2 or 1.5.';
    } else if (adj.mode !== 'set' && quantity === 0) {
      next.quantity = 'Enter an amount above 0.';
    }
    if (ing && !adj.unit) next.unit = 'Choose a unit.';
    else if (ing && quantityText && !next.quantity && base === null)
      next.unit = 'This unit does not convert for this ingredient yet.';
    if (reason.needsNote && !adj.note.trim()) next.note = 'Say why you are adding stock.';
    const costText = adj.cost.trim().replace(/^\$/, '');
    const cost = Number(costText);
    const freeCost = adj.mode === 'add' && costText !== '' && cost === 0;
    if (adj.mode === 'add' && costText && !(Number.isFinite(cost) && cost >= 0)) {
      next.cost = 'Enter a price like 3.49, or leave it empty.';
    } else if (freeCost && !adj.free) {
      next.cost = 'A price of $0 means it was free. Tick “It was free”, or leave the price empty.';
    }
    setErrors(next);
    if (Object.keys(next).length > 0 || !ing) return;
    if (!locationId) {
      setAdjMsg({ kind: 'error', text: noLocationText });
      return;
    }
    const amount = `${formatNumber(quantity, 3)} ${unitShort(adj.unit, ing)}`;
    adjust.mutate({
      ing,
      mode: adj.mode,
      amount,
      body: {
        location_id: locationId,
        ingredient_id: ing.id,
        mode: adj.mode,
        quantity,
        ...splitUnitValue(adj.unit),
        reason: reason.code,
        note: adj.note.trim() || undefined,
        unit_cost_cents:
          adj.mode === 'add' && costText ? Math.round(cost * 100 * 10000) / 10000 : undefined,
        free: freeCost && adj.free ? true : undefined,
      },
    });
  };

  const show = (n: number | null | undefined, s: Stock) =>
    n === null || n === undefined ? 'Not set' : formatQty(n, s, style);

  return (
    <Section title="Inventory">
      <style>{INVENTORY_CSS}</style>
      {stock.isError && (
        <Notice kind="error">Could not load stock. {errorText(stock.error, '')}</Notice>
      )}
      <Card>
        <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 14 }}>
          On hand is what the kitchen has. Reserved is held for confirmed orders, and available is
          what is left. Costs come from your receipts on the Purchasing page. Nothing here stops an
          order unless you turn on the stock check in Readiness.
          {canPrice &&
            ' Stock you had before Crave, or added without a price, can get one with Add purchase price.'}
        </p>
      </Card>
      <StockMeasureIn />
      {priceMsg && (
        <div role="status">
          <Notice kind="ok">{priceMsg}</Notice>
        </div>
      )}
      <div className="inv-stock">
        <div style={{ overflowX: 'auto' }}>
          <table className="inv-table">
            <thead>
              <tr>
                {STOCK_HEAD.map((h, i) => (
                  <th key={i}>{h || <span className="inv-hidden">Actions</span>}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(items ?? []).length === 0 && (
                <tr>
                  <td colSpan={STOCK_HEAD.length} className="inv-empty">
                    {stock.isLoading ? 'Loading…' : 'No ingredients yet.'}
                  </td>
                </tr>
              )}
              {(items ?? []).map((s) => {
                const lotCount = s.lots?.length ?? 0;
                const cost = costText(s, style);
                return (
                  <Fragment key={s.id}>
                    <tr>
                      <td className="inv-name">
                        <strong>{s.name}</strong>
                        {s.is_prep && (
                          <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
                            {' '}
                            · made here
                          </span>
                        )}
                        {s.category && (
                          <div style={{ fontSize: 12, color: 'var(--ink-soft)' }}>{s.category}</div>
                        )}
                      </td>
                      <td className="inv-amt inv-oh">
                        <span className="inv-label">On hand</span>
                        <span style={{ color: s.on_hand_qty < 0 ? 'var(--crave-red)' : undefined }}>
                          {show(s.on_hand_qty, s)}
                        </span>
                      </td>
                      <td className="inv-amt inv-res">
                        <span className="inv-label">Reserved</span>
                        {show(s.reserved_qty, s)}
                      </td>
                      <td className="inv-amt inv-av">
                        <span className="inv-label">Available</span>
                        <span
                          style={{ color: s.available_qty < 0 ? 'var(--crave-red)' : undefined }}
                        >
                          {show(s.available_qty, s)}
                        </span>
                      </td>
                      <td className="inv-amt inv-cost">
                        <span className="inv-label">Cost</span>
                        {cost ?? <span style={{ color: 'var(--warn)' }}>Not known yet</span>}
                        {canPrice && !s.is_prep && s.uncovered_qty > 1e-6 && (
                          <span className="inv-unpriced">
                            {cost && (
                              <span className="inv-sub">
                                {formatQty(s.uncovered_qty, s, style)} has no price
                              </span>
                            )}
                            <button
                              type="button"
                              className="inv-link"
                              aria-expanded={pricing?.id === s.id && pricing.lotId === null}
                              aria-label={`Add purchase price for ${s.name}`}
                              onClick={() => {
                                setPriceMsg(null);
                                setPricing(
                                  pricing?.id === s.id && pricing.lotId === null
                                    ? null
                                    : { id: s.id, lotId: null },
                                );
                              }}
                            >
                              Add purchase price
                            </button>
                          </span>
                        )}
                      </td>
                      <td className="inv-lots">
                        <span className="inv-label">Lots</span>
                        {lotCount === 0 ? (
                          <span style={{ color: 'var(--ink-soft)' }}>None</span>
                        ) : (
                          <button
                            type="button"
                            className="inv-link"
                            aria-expanded={open === s.id}
                            onClick={() => setOpen(open === s.id ? null : s.id)}
                          >
                            {plural(lotCount, 'lot')}
                            {open === s.id ? ' ▲' : ' ▼'}
                          </button>
                        )}
                      </td>
                      <td className="inv-act">
                        {canAdjust && (
                          <Button
                            type="button"
                            variant="secondary"
                            style={{ padding: '0 14px' }}
                            aria-label={`Adjust ${s.name}`}
                            onClick={() => startAdjust(s)}
                          >
                            Adjust
                          </Button>
                        )}
                      </td>
                    </tr>
                    {/* Lots open right under their ingredient, so the tap shows something where it happened. */}
                    {open === s.id && (
                      <tr className="inv-lots-row">
                        <td colSpan={STOCK_HEAD.length} className="inv-lots-cell">
                          <div className="inv-lots-head">
                            <strong>{s.name}: lots on hand</strong>
                            <button
                              type="button"
                              className="inv-link"
                              onClick={() => setOpen(null)}
                            >
                              Hide
                            </button>
                          </div>
                          <ul className="inv-lots-list">
                            {(s.lots ?? []).map((l) => (
                              <li key={l.id}>
                                <strong>{show(l.remaining_qty, s)} left</strong> of{' '}
                                {show(l.initial_qty, s)}
                                {l.is_opening_price
                                  ? ` · Price added for stock already on hand${l.vendor ? `, from ${l.vendor}` : ''}`
                                  : ` · Received ${when(l.received_at)} ${l.vendor ? `from ${l.vendor}` : '(added by hand)'}`}
                                {` · ${l.price_entered?.free ? 'Free' : money(l.total_cost_cents)}`}
                                {l.price_corrected_at
                                  ? ` · Price corrected ${when(l.price_corrected_at)}`
                                  : ''}
                                {` · Use by ${l.expires_at ? when(l.expires_at) : 'not set'}`}
                                {l.from_receipt ? (
                                  <span className="inv-sub">
                                    Price from a receipt. Correct it on the Purchasing page.
                                  </span>
                                ) : canPrice && l.can_correct_price ? (
                                  <span className="inv-lot-actions">
                                    <button
                                      type="button"
                                      className="inv-link"
                                      aria-label={`Correct the price of this lot of ${s.name}`}
                                      onClick={() => {
                                        setPriceMsg(null);
                                        setPricing({ id: s.id, lotId: l.id });
                                      }}
                                    >
                                      Correct price
                                    </button>
                                  </span>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                    {pricing?.id === s.id && (
                      <tr className="inv-lots-row">
                        <td colSpan={STOCK_HEAD.length} className="inv-lots-cell">
                          <PriceForm
                            key={pricing.lotId ?? 'new'}
                            s={s}
                            lotId={pricing.lotId}
                            locationId={locationId}
                            vendors={vendors.data ?? []}
                            style={style}
                            onSaved={(text) => {
                              setPricing(null);
                              setPriceMsg(text);
                            }}
                            onClose={() => setPricing(null)}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {canAdjust && (
        <Card>
          <form
            ref={formRef}
            className="inv-adjust-form"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              submitAdjust();
            }}
          >
            <div style={{ display: 'grid', gap: 4, marginBottom: 12 }}>
              <strong>Adjust stock</strong>
              <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-soft)' }}>
                Take stock out for waste or a staff meal, add stock you found, or set the count
                after counting the shelf.
              </p>
            </div>
            <FormGrid min={220}>
              <Select
                label="Ingredient"
                value={adj.ingredient_id}
                onChange={chooseIngredient}
                options={(items ?? []).map((s) => [s.id, s.name])}
                error={errors.ingredient}
              />
              <SelectField
                label="Action"
                value={adj.mode}
                onChange={(e) => chooseMode(e.target.value as Mode)}
                options={MODES}
              />
              <Field
                id={quantityId}
                label="Quantity"
                inputMode="decimal"
                autoComplete="off"
                value={adj.quantity}
                onChange={(e) => {
                  setAdj({ ...adj, quantity: e.target.value });
                  if (errors.quantity) setErrors({ ...errors, quantity: undefined });
                }}
                hint={preview}
                error={errors.quantity}
              />
              <UnitSelect
                ingredient={ing}
                value={adj.unit}
                onValueChange={(v) => {
                  setAdj({ ...adj, unit: v });
                  if (errors.unit) setErrors({ ...errors, unit: undefined });
                }}
                placeholder={ing ? undefined : 'Choose an ingredient first'}
                error={errors.unit}
              />
              <SelectField
                label="Reason"
                value={reason.key}
                onChange={(e) => setAdj({ ...adj, reason: e.target.value })}
                options={reasons.map((r) => [r.key, r.label] as const)}
              />
              {adj.mode === 'add' && (
                <Field
                  label={`Cost per ${adj.unit ? unitShort(adj.unit, ing) : 'unit'} (optional)`}
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder="$"
                  value={adj.cost}
                  onChange={(e) => setAdj({ ...adj, cost: e.target.value })}
                  hint="With a price, food cost counts this stock."
                  error={errors.cost}
                />
              )}
              <Field
                label={reason.needsNote ? 'Note' : 'Note (optional)'}
                value={adj.note}
                maxLength={300}
                onChange={(e) => {
                  setAdj({ ...adj, note: e.target.value });
                  if (errors.note) setErrors({ ...errors, note: undefined });
                }}
                error={errors.note}
              />
            </FormGrid>
            {adj.mode === 'add' &&
              adj.cost.trim().replace(/^\$/, '') !== '' &&
              Number(adj.cost.trim().replace(/^\$/, '')) === 0 && (
                <label className="inv-check">
                  <input
                    type="checkbox"
                    checked={adj.free}
                    onChange={(e) => {
                      setAdj({ ...adj, free: e.target.checked });
                      if (errors.cost) setErrors({ ...errors, cost: undefined });
                    }}
                  />
                  It was free (a gift or a sample)
                </label>
              )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button type="submit" disabled={adjust.isPending}>
                {adjust.isPending
                  ? 'Saving…'
                  : adj.mode === 'remove'
                    ? 'Remove from stock'
                    : adj.mode === 'add'
                      ? 'Add to stock'
                      : 'Save count'}
              </Button>
            </div>
            {adjMsg && (
              <div role="status" style={{ marginTop: 12 }}>
                <Notice kind={adjMsg.kind}>{adjMsg.text}</Notice>
              </div>
            )}
          </form>
        </Card>
      )}
      <Card>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setProdMsg(null);
            const batches = Number(prod.batches.trim());
            if (!locationId) {
              setProdMsg({ kind: 'error', text: noLocationText });
            } else if (!prod.recipe_id) {
              setProdMsg({
                kind: 'error',
                text: prepRecipes.isError
                  ? `Prep recipes did not load. ${errorText(prepRecipes.error, '')}`
                  : 'Choose a prep recipe.',
              });
            } else if (!prod.batches.trim() || !(batches > 0 && batches <= 100)) {
              setProdMsg({
                kind: 'error',
                text: 'Batches must be more than 0 and no more than 100.',
              });
            } else {
              produce.mutate({
                location_id: locationId,
                recipe_id: prod.recipe_id,
                batches,
              });
            }
          }}
        >
          <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
            <strong>Record a production run</strong>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-soft)' }}>
              Mixed dough, made sauce or hot honey? This takes the raw ingredients out of stock and
              adds what you made.
            </p>
            {prepRecipes.isError && (
              <Notice kind="error">
                Could not load prep recipes. {errorText(prepRecipes.error, '')}
              </Notice>
            )}
          </div>
          <FormGrid min={230}>
            <Select
              label="Prep recipe"
              value={prod.recipe_id}
              onChange={(v) => setProd({ ...prod, recipe_id: v })}
              options={(prepRecipes.data ?? []).map((r) => [r.id, r.name])}
              hint={
                prepRecipes.isSuccess && prepRecipes.data.length === 0
                  ? 'No prep recipes to choose from yet.'
                  : undefined
              }
            />
            <Field
              label="Batches"
              inputMode="decimal"
              value={prod.batches}
              onChange={(e) => setProd({ ...prod, batches: e.target.value })}
            />
          </FormGrid>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button type="submit" variant="secondary" disabled={produce.isPending}>
              {produce.isPending ? 'Saving…' : 'Record production'}
            </Button>
          </div>
          {prodMsg && (
            <div role="status" style={{ marginTop: 12 }}>
              <Notice kind={prodMsg.kind}>{prodMsg.text}</Notice>
            </div>
          )}
        </form>
      </Card>

      {can('shopping.manage') && (
        <Card style={{ minWidth: 0 }}>
          <strong>Shopping list</strong>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--ink-soft)' }}>
            What to buy for upcoming orders and the minimums you keep, in store sizes and by store,
            is on its own page. <Link to="/shopping">Open the shopping list</Link>
          </p>
        </Card>
      )}
    </Section>
  );
}

type PriceErrors = Partial<Record<'price' | 'quantity' | 'unit' | 'date', string | undefined>>;

/**
 * Add a purchase price for the stock on hand that has none (lotId null), or correct a hand-entered
 * price on one lot. What he paid in supermarket terms: a price for an amount ($4.98 for 1 tub,
 * $3.99 per lb) or the total. Amounts and the preview follow the "Measure in" choice.
 */
function PriceForm({
  s,
  lotId,
  locationId,
  vendors,
  style,
  onSaved,
  onClose,
}: {
  s: Stock;
  lotId: string | null;
  locationId: string | undefined;
  vendors: Vendor[];
  style: MeasureStyle;
  onSaved: (text: string) => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const lot = lotId ? ((s.lots ?? []).find((l) => l.id === lotId) ?? null) : null;
  const entered = lot?.price_entered ?? null;
  const [priceFor, setPriceFor] = useState<'amount' | 'total'>(entered?.price_for ?? 'amount');
  const [price, setPrice] = useState(
    entered && !entered.free ? (entered.price_cents / 100).toFixed(2) : '',
  );
  const [quantity, setQuantity] = useState(entered?.quantity ? String(entered.quantity) : '1');
  const [unit, setUnit] = useState(
    entered?.package_id
      ? unitValue(null, entered.package_id)
      : entered?.unit
        ? unitValue(entered.unit)
        : defaultUnitValue(s),
  );
  const [free, setFree] = useState(entered?.free ?? false);
  const [store, setStore] = useState(lot?.vendor ?? '');
  const [date, setDate] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<PriceErrors>({});
  const [failure, setFailure] = useState<string | null>(null);
  const choiceName = useId();
  const storeList = useId();

  // A correction prices the lot's starting amount; a new price, what has none.
  const covered = lot ? lot.initial_qty : s.uncovered_qty;
  const coveredText = formatQty(covered, s, style);

  const save = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      lot
        ? (await api.patch<PriceResult>(`/admin/inventory/lots/${lot.id}/price`, body)).data
        : (await api.post<PriceResult>('/admin/inventory/opening-prices', body)).data,
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['stock'] });
      void qc.invalidateQueries({ queryKey: ['vendors'] });
      const per =
        r.total_cost_cents > 0
          ? costText({ ...s, cost_cents_per_base: r.cost_cents_per_base }, style)
          : null;
      const cost = `${money(r.total_cost_cents)}${per ? ` (${per})` : ''}`;
      onSaved(
        lot
          ? `Price corrected. That lot of ${s.name} now costs ${cost}. On hand did not change.`
          : `Price saved. ${formatQty(r.qty_base ?? covered, s, style)} of ${s.name} now costs ${cost}. On hand did not change.`,
      );
    },
    onError: (e) => {
      setFailure(errorText(e, 'Could not save. Check the connection.'));
      // The amount with no price may have changed since the page loaded: show the current one.
      void qc.invalidateQueries({ queryKey: ['stock'] });
    },
  });

  const priceText = price.trim().replace(/^\$/, '');
  const priceNumber = Number(priceText);
  const cents = free
    ? 0
    : priceText !== '' && Number.isFinite(priceNumber) && priceNumber >= 0
      ? Math.round(priceNumber * 100)
      : null;
  // A free price has no amount to convert: it is $0 for all of it.
  const basis = free ? 'total' : priceFor;
  const qtyText = quantity.trim();
  const qtyNumber = Number(qtyText);
  const base =
    basis === 'amount' && qtyText && Number.isFinite(qtyNumber) && qtyNumber > 0 && unit
      ? toBase(s, qtyNumber, unit)
      : null;
  const total =
    cents === null
      ? null
      : basis === 'total'
        ? cents
        : base
          ? Math.round((cents / base) * covered)
          : null;
  const perText =
    total && covered > 0 ? costText({ ...s, cost_cents_per_base: total / covered }, style) : null;
  const preview = free
    ? `Saved as free: ${coveredText} at $0.`
    : cents === 0
      ? 'A price of $0 means it was free. Tick “It was free” below.'
      : total === null
        ? basis === 'total'
          ? `What you paid for all ${coveredText}.`
          : 'What you paid for the amount you choose.'
        : total < 1
          ? `That is less than 1 cent for ${coveredText}. Check the price and the amount.`
          : `That comes to ${money(total)} for ${coveredText}${perText ? `, or ${perText}` : ''}.`;

  const submit = () => {
    setFailure(null);
    const next: PriceErrors = {};
    if (!free) {
      if (priceText === '' || !Number.isFinite(priceNumber) || priceNumber < 0) {
        next.price = 'Enter what you paid, like 4.98.';
      } else if (cents === 0) {
        next.price =
          'A price of $0 means it was free. Tick “It was free” below, or enter the price.';
      }
    }
    if (basis === 'amount') {
      if (!qtyText || !Number.isFinite(qtyNumber) || qtyNumber <= 0) {
        next.quantity = 'Enter how many, like 1 or 2.5.';
      }
      if (!unit) next.unit = 'Choose a unit or package.';
      else if (!next.quantity && base === null) {
        next.unit = 'This unit does not convert for this ingredient yet.';
      }
    }
    if (date && date > today()) next.date = 'The date you bought it cannot be after today.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    if (!lot && !locationId) {
      setFailure('Stock did not load, so this cannot be saved. Reload the page and try again.');
      return;
    }
    const name = store.trim();
    const match = vendors.find((v) => v.name.trim().toLowerCase() === name.toLowerCase());
    const storeFields = name
      ? match
        ? { vendor_id: match.id }
        : { vendor_name: name }
      : lot?.vendor_id
        ? { vendor_id: null }
        : {};
    save.mutate({
      ...(lot
        ? {}
        : { location_id: locationId, ingredient_id: s.id, expected_qty: s.uncovered_qty }),
      price_cents: cents ?? 0,
      price_for: basis,
      ...(basis === 'amount' ? { quantity: qtyNumber, ...splitUnitValue(unit) } : {}),
      ...(free ? { free: true } : {}),
      ...storeFields,
      ...(date ? { purchased_on: date } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    });
  };

  const head = (
    <div className="inv-lots-head">
      <strong>{lot ? `Correct price: ${s.name}` : `Add purchase price: ${s.name}`}</strong>
      <button type="button" className="inv-link" onClick={onClose}>
        Close
      </button>
    </div>
  );
  if (lotId && !lot) {
    return (
      <div className="inv-price-form">
        {head}
        <p>
          That lot has been used up, so a new price would not change anything. Past reports keep the
          cost it had.
        </p>
      </div>
    );
  }
  if (!lot && covered <= 1e-6) {
    return (
      <div className="inv-price-form">
        {head}
        <p>All of the {s.name} on hand has a price now.</p>
      </div>
    );
  }

  return (
    <form
      className="inv-price-form"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      {head}
      <p>
        {lot
          ? `This lot started with ${coveredText}${
              lot.remaining_qty < lot.initial_qty
                ? ` and has ${formatQty(lot.remaining_qty, s, style)} left`
                : ''
            }. Enter what you paid for it.`
          : `${coveredText} of ${s.name} on hand has no price yet. Enter what you paid, and food cost will use it. On hand does not change.`}
      </p>
      {!free && (
        <fieldset className="crave-measure inv-choice">
          <legend>The price is</legend>
          <div className="crave-measure-options">
            {(
              [
                ['amount', 'For an amount, like per lb or per tub'],
                ['total', `The total for all ${coveredText}`],
              ] as const
            ).map(([v, label]) => (
              <label key={v} className="crave-measure-option">
                <input
                  type="radio"
                  name={choiceName}
                  value={v}
                  checked={priceFor === v}
                  onChange={() => {
                    setPriceFor(v);
                    setErrors({});
                  }}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </fieldset>
      )}
      <FormGrid min={200}>
        <Field
          label="Price paid"
          inputMode="decimal"
          autoComplete="off"
          placeholder="$"
          disabled={free}
          value={free ? '0' : price}
          onChange={(e) => {
            setPrice(e.target.value);
            if (errors.price) setErrors({ ...errors, price: undefined });
          }}
          hint={preview}
          error={errors.price}
        />
        {basis === 'amount' && (
          <>
            <Field
              label="For how many"
              inputMode="decimal"
              autoComplete="off"
              value={quantity}
              onChange={(e) => {
                setQuantity(e.target.value);
                if (errors.quantity) setErrors({ ...errors, quantity: undefined });
              }}
              hint="Like 1 tub, or 1 lb."
              error={errors.quantity}
            />
            <UnitSelect
              label="Unit or package"
              ingredient={s}
              value={unit}
              onValueChange={(v) => {
                setUnit(v);
                if (errors.unit) setErrors({ ...errors, unit: undefined });
              }}
              error={errors.unit}
            />
          </>
        )}
        <Field
          label="Store (optional)"
          list={storeList}
          autoComplete="off"
          maxLength={120}
          value={store}
          onChange={(e) => setStore(e.target.value)}
          hint="Where you bought it."
        />
        <Field
          label="Date bought (optional)"
          type="date"
          max={today()}
          value={date}
          onChange={(e) => {
            setDate(e.target.value);
            if (errors.date) setErrors({ ...errors, date: undefined });
          }}
          hint={
            lot
              ? 'Leave it empty to keep the date this lot has.'
              : 'If you do not know, leave it empty. This stock is then used before newer stock.'
          }
          error={errors.date}
        />
        <Field
          label="Note (optional)"
          maxLength={300}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </FormGrid>
      <datalist id={storeList}>
        {vendors.map((v) => (
          <option key={v.id} value={v.name} />
        ))}
      </datalist>
      <label className="inv-check">
        <input
          type="checkbox"
          checked={free}
          onChange={(e) => {
            setFree(e.target.checked);
            setErrors({});
          }}
        />
        It was free (a gift or a sample)
      </label>
      <p>
        {lot
          ? 'Stock from this lot that was already used keeps the cost it had in past reports.'
          : 'Stock already used before you enter this price keeps its old unknown cost in past reports.'}
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save price'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
      {failure && (
        <div role="status">
          <Notice kind="error">{failure}</Notice>
        </div>
      )}
    </form>
  );
}

/** A labelled dropdown with a "Choose…" first option, aligned like every other field. */
export function Select({
  label,
  value,
  onChange,
  options,
  hint,
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
  hint?: string | undefined;
  error?: string | undefined;
}) {
  return (
    <SelectField
      label={label}
      hint={hint}
      error={error}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      options={options}
      placeholder="Choose…"
    />
  );
}
